import {isFeatureEnabled} from '@github-ui/feature-flags'
import {applyOperation} from '@github-ui/optimistic-connection'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {useCallback} from 'react'

import type {LabelsQueryData} from './types'
import {
  buildDeleteLabelOperation,
  LABELS_CONNECTION_PATH,
  LABELS_OPTIMISTIC_CONNECTION_FLAG,
} from './utils/label-connection-operations'
import {deleteLabelRequest} from './utils/label-requests'
import {updateLabelsCache} from './utils/update-labels-cache'

type UseDeleteLabelOptions = {
  labelId: string
  repositoryId: string
  repositoryNameWithOwner: string
  queryKey?: RouteQueryKey
  setIsDialogOpen: (v: boolean) => void
  setErrorsPresent: (v: boolean) => void
}

type DeleteLabelHandler = () => void

type DeleteLabelContext = {
  previousData: LabelsQueryData | undefined
}

/** Selects the delete strategy by feature flag; see `useCreateLabel` for the both-mounted pattern. */
export function useDeleteLabel(options: UseDeleteLabelOptions): DeleteLabelHandler {
  const legacy = useDeleteLabelLegacy(options)
  const optimistic = useDeleteLabelOptimistic(options)
  return isFeatureEnabled(LABELS_OPTIMISTIC_CONNECTION_FLAG) ? optimistic : legacy
}

function useDeleteLabelOptimistic({
  labelId,
  repositoryId,
  repositoryNameWithOwner,
  queryKey,
  setIsDialogOpen,
  setErrorsPresent,
}: UseDeleteLabelOptions): DeleteLabelHandler {
  const queryClient = useQueryClient()

  const {mutate} = useConnectionMutation<void, Error>({
    ownerId: repositoryId,
    connectionPath: LABELS_CONNECTION_PATH,
    mutationFn: () => deleteLabelRequest(repositoryNameWithOwner, labelId),
    onError: () => {
      setErrorsPresent(true)
    },
    onSuccess: () => {
      if (queryKey) {
        queryClient.setQueryData<LabelsQueryData>(queryKey, base =>
          base ? applyOperation(base, repositoryId, buildDeleteLabelOperation(labelId)) : base,
        )
      }
    },
  })

  return useCallback(() => {
    setIsDialogOpen(false)
    mutate({operation: buildDeleteLabelOperation(labelId)})
  }, [mutate, labelId, setIsDialogOpen])
}

/** The flag-off fallback: hand-rolled cache surgery with snapshot rollback. */
function useDeleteLabelLegacy({
  labelId,
  repositoryNameWithOwner,
  queryKey,
  setIsDialogOpen,
  setErrorsPresent,
}: UseDeleteLabelOptions): DeleteLabelHandler {
  const queryClient = useQueryClient()

  const {mutate} = useMutation<void, Error, void, DeleteLabelContext>({
    mutationFn: () => deleteLabelRequest(repositoryNameWithOwner, labelId),
    onMutate: async (): Promise<DeleteLabelContext> => {
      setIsDialogOpen(false)

      const previousData = queryKey ? queryClient.getQueryData<LabelsQueryData>(queryKey) : undefined

      // Optimistically remove the label from the TanStack cache
      if (queryKey) {
        queryClient.setQueryData<LabelsQueryData>(
          queryKey,
          updateLabelsCache(labels => {
            labels.totalCount = (labels.totalCount ?? 0) - 1
            labels.edges = labels.edges.filter(edge => edge?.node?.id !== labelId)
          }),
        )
      }

      return {previousData}
    },
    onError: (_error, _variables, context) => {
      if (queryKey && context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData)
      }
      setErrorsPresent(true)
    },
  })

  return useCallback(() => {
    mutate()
  }, [mutate])
}
